import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Button, Image, Tooltip, Spin, Tag, App } from 'antd';
import { fileApi } from '@services/Conference';
import { prepareBCOSSUrl } from '@utils/utils';
import { EMPTY_PLACEHOLDER } from '../../constants';
// TODO: 正式上线前恢复白名单校验引用
// import { FORUM_EXPERT_BENEFIT_NAMES } from '../../constants';

/**
 * 判断 fileApi 是否上传成功
 * 拦截器已弹错的场景（网络异常/超时等）post 会返回 string，不再重复提示
 * @param {*} res - fileApi 返回值
 * @returns {boolean}
 */
function isFileApiUploadSuccess(res) {
    return res && typeof res === 'object' && res.status_code === 200;
}

/**
 * @param {string|array} boothNumbers
 * @returns {string}
 */
function formatBoothNumbers(boothNumbers) {
    if (!boothNumbers) return EMPTY_PLACEHOLDER;
    try {
        const arr = Array.isArray(boothNumbers) ? boothNumbers : JSON.parse(boothNumbers);
        return Array.isArray(arr) && arr.length > 0 ? arr.join('、') : EMPTY_PLACEHOLDER;
    } catch {
        return boothNumbers || EMPTY_PLACEHOLDER;
    }
}

/**
 * 单条权益兑现行组件
 * 上方只读权益信息，下方图片缩略图 + 上传中占位 + 上传按钮
 * @param {Object} props
 * @param {Object} props.benefit - 权益数据
 * @param {Object} props.state - 权益兑现状态 { images, experts, changed }
 * @param {Function} props.onChange - 状态变更回调（支持对象或函数式更新）
 * @param {Function} props.onAssociateExpert - 关联专家回调
 * @param {Function} props.onUploadProgressChange - 上传进度回调 ('start' | 'finish' | 'fail' | 'remove')
 */
export default function RealizationBenefitRow({ benefit, state = {}, onChange, onAssociateExpert, onUploadProgressChange }) {
    const { message } = App.useApp();
    /** 上传中的文件列表，用于 UI 占位和槽位计算 */
    const [uploadingFiles, setUploadingFiles] = useState([]);
    /** ref 同步记录上传中数量，避免 beforeUpload 闭包拿到过期的 state */
    const uploadingCountRef = useRef(0);
    /** 本次弹窗内上传成功的图片 URL，删除时需同步回退进度 */
    const sessionUploadedUrlsRef = useRef(new Set());
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const { benefitName, type, isBoothBenefit, quantity, boothNumbers, hallsId, remark } = benefit;
    const { images = [], experts = [] } = state;
    /** 剩余可上传数 = 上限 5 - 已有图片 - 上传中文件 */
    const remainingSlots = 5 - images.length - uploadingFiles.length;
    const uploadDisabled = remainingSlots <= 0;
    /** 上传按钮禁用时提示：已满 5 张 vs 上传中占满剩余槽位 */
    const uploadDisabledTip = uploadDisabled ? (images.length >= 5 ? '最多上传 5 张图片' : '上传中，请稍候') : '';

    /**
     * 判断是否为白名单论坛权益（可关联显示）
     * TODO: 正式上线前恢复白名单校验：type === 4 && FORUM_EXPERT_BENEFIT_NAMES.includes(benefitName)
     */
    const canAssociateExpert = type === 4;

    /**
     * 专家姓名展示
     */
    const expertNames = experts.map(expert => expert.name).join('、');

    /** 有上传图片即已兑现，与进度统计规则一致 */
    const isRealized = images.length > 0;

    /**
     * 图片上传前校验
     * 通过 ref 实时获取上传中数量，确保连续多批选择时槽位计算准确
     */
    const beforeUpload = (file, batchFileList) => {
        if (!file.type?.startsWith('image/')) {
            message.error(`${file.name} 仅支持图片格式`);
            return Upload.LIST_IGNORE;
        }

        const isLt10M = file.size / 1024 / 1024 <= 10;
        if (!isLt10M) {
            message.error(`${file.name} 超过 10MB`);
            return Upload.LIST_IGNORE;
        }

        const indexInBatch = batchFileList.indexOf(file);
        const currentRemaining = 5 - images.length - uploadingCountRef.current;
        if (indexInBatch >= currentRemaining) {
            if (indexInBatch === currentRemaining && currentRemaining >= 0) {
                const overCount = batchFileList.length - currentRemaining;
                message.warning(`最多上传 5 张图片，已自动忽略多余的 ${overCount} 张`);
            }
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    /**
     * 通知父组件上传进度变化
     */
    const notifyUploadStart = useCallback(() => {
        onUploadProgressChange?.('start');
    }, [onUploadProgressChange]);

    const notifyUploadFinish = useCallback(() => {
        onUploadProgressChange?.('finish');
    }, [onUploadProgressChange]);

    const notifyUploadRemove = useCallback(() => {
        onUploadProgressChange?.('remove');
    }, [onUploadProgressChange]);

    const notifyUploadFail = useCallback(() => {
        onUploadProgressChange?.('fail');
    }, [onUploadProgressChange]);

    /**
     * 单个文件上传（选择即上传）
     */
    const customRequest = async ({ file }) => {
        const uid = file.uid || `${Date.now()}-${file.name}`;
        let succeeded = false;

        uploadingCountRef.current += 1;
        setUploadingFiles(prev => [...prev, { uid, name: file.name }]);
        notifyUploadStart();

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 14);

            const res = await fileApi(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (!mountedRef.current) return;

            if (!isFileApiUploadSuccess(res)) {
                // 422 等业务错误由接口返回对象，拦截器未弹窗，此处补充文件名提示
                if (typeof res === 'object' && res !== null && typeof res.status_code === 'number') {
                    message.error(`${file.name} 上传失败`);
                }
                return;
            }

            const url = prepareBCOSSUrl(res.data);
            sessionUploadedUrlsRef.current.add(url);
            onChange(prev => ({
                ...prev,
                images: [...(prev.images || []), url],
                changed: true,
            }));
            succeeded = true;
        } catch (error) {
            if (!mountedRef.current) return;
            console.error('Image upload error:', error);
            message.error(`${file.name} 上传失败`);
        } finally {
            uploadingCountRef.current -= 1;
            if (mountedRef.current) {
                setUploadingFiles(prev => prev.filter(f => f.uid !== uid));
            }
            if (succeeded) {
                notifyUploadFinish();
            } else {
                notifyUploadFail();
            }
        }
    };

    /**
     * 删除已上传图片；若为本 session 上传的图片，同步回退上传进度
     */
    const handleRemove = imgUrl => {
        if (sessionUploadedUrlsRef.current.has(imgUrl)) {
            sessionUploadedUrlsRef.current.delete(imgUrl);
            notifyUploadRemove();
        }
        onChange(prev => ({
            ...prev,
            images: (prev.images || []).filter(img => img !== imgUrl),
            changed: true,
        }));
    };

    /**
     * 关联专家按钮点击
     */
    const handleAssociateClick = () => {
        if (!hallsId) {
            message.warning('该权益未关联会议');
            return;
        }
        onAssociateExpert(benefit);
    };

    return (
        <div className="benefit-row">
            <Tag className="benefit-row-status-tag" color={isRealized ? 'success' : 'default'}>
                {isRealized ? '已兑现' : '未兑现'}
            </Tag>
            {/* 上半部分：权益信息（只读） */}
            <div className="benefit-row-info">
                <div className="benefit-row-detail">
                    <span className="benefit-row-label">权益：</span>
                    <span>{benefitName}</span>
                </div>
                {isBoothBenefit === 1 ? (
                    <div className="benefit-row-detail">
                        <span className="benefit-row-label">展位号：</span>
                        <span>{formatBoothNumbers(boothNumbers)}</span>
                    </div>
                ) : (
                    <div className="benefit-row-detail">
                        <span className="benefit-row-label">购买数量：</span>
                        <span>{quantity || EMPTY_PLACEHOLDER}</span>
                    </div>
                )}
                <div className="benefit-row-detail">
                    <span className="benefit-row-label">说明：</span>
                    <span>{remark || EMPTY_PLACEHOLDER}</span>
                </div>
                {canAssociateExpert && (
                    <div className="benefit-row-detail">
                        <span className="benefit-row-label">专家：</span>
                        <span>{expertNames || EMPTY_PLACEHOLDER}</span>
                    </div>
                )}
            </div>

            {/* 下半部分：图片上传区域 */}
            <div className="benefit-row-upload">
                <div className="benefit-row-images">
                    <Image.PreviewGroup>
                        {images.map(url => (
                            <div key={url} className="benefit-img-item">
                                <Image src={url} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                                <span className="benefit-img-remove" onClick={() => handleRemove(url)}>
                                    <i className="bi bi-x-circle-fill" />
                                </span>
                            </div>
                        ))}
                    </Image.PreviewGroup>
                    {/* 上传中占位 */}
                    {uploadingFiles.map(f => (
                        <div key={f.uid} className="benefit-img-item benefit-img-uploading">
                            <Spin size="small" />
                        </div>
                    ))}
                </div>
                <div className="benefit-row-actions">
                    {canAssociateExpert && (
                        <Tooltip title={!hallsId ? '该权益未关联会议' : ''}>
                            <Button type="link" size="small" onClick={handleAssociateClick} disabled={!hallsId}>
                                关联专家
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={uploadDisabledTip}>
                        <span>
                            <Upload multiple beforeUpload={beforeUpload} customRequest={customRequest} showUploadList={false} disabled={uploadDisabled} accept="image/*">
                                <Button type="link" size="small" disabled={uploadDisabled} loading={uploadingFiles.length > 0}>
                                    上传图片
                                </Button>
                            </Upload>
                        </span>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
}
